import { useEffect, useRef, useState } from "react";
import { useParticipant } from "@videosdk.live/react-sdk";
import { useAppContext } from "../AppContextDef";
import {
  packetLossPercent,
  formatResolution,
  extractFps,
  extractBitrateKbps,
  rateVideo,
  rateAudio,
  formatDeviceLabel,
} from "../utils/participantStats";

const POLL_MS = 1000;

// Recorder-side: assemble the REMOTE participant's (customer's) live stats for
// the panel. All media values come from useParticipant(remoteParticipantId) —
// never the recorder's own local participant.
export default function useRecorderParticipantStats(remoteParticipantId) {
  const {
    deviceInfo,
    cpuStatus,
    networkStatus,
    livenessResult,
    faceMatchScore,
    faceMatchVerdict,
  } = useAppContext();

  const { getVideoStats, getAudioStats, webcamOn } =
    useParticipant(remoteParticipantId);
  
  

  const [live, setLive] = useState({ video: null, audio: null });
  const loggedRef = useRef(false);

  useEffect(() => {
    if (!remoteParticipantId || !webcamOn || !getVideoStats || !getAudioStats) {
      return;
    }
    let cancelled = false;

    const tick = async () => {
      try {
        const [vArr, aArr] = await Promise.all([
          getVideoStats(),
          getAudioStats(),
        ]);
        console.log("[Recorder] remote participant stats tick video", vArr);
        console.log("[Recorder] remote participant stats tick audio", aArr);
        const video = Array.isArray(vArr) && vArr.length ? vArr[0] : null;
        const audio = Array.isArray(aArr) && aArr.length ? aArr[0] : null;
        if (!loggedRef.current) {
          // One-time log to confirm exact runtime keys (fps/size) on a real journey.
          console.debug("[Recorder] remote participant stats shape", { video, audio });
          loggedRef.current = true;
        }
        if (!cancelled) setLive({ video, audio });
      } catch (err) {
        console.error("[Recorder] failed to read remote participant stats", err);
      }
    };

    tick();
    const id = setInterval(tick, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [remoteParticipantId, webcamOn, getVideoStats, getAudioStats]);

  const { video, audio } = live;

  // null until the /verify API has answered at least once, which the panel
  // renders as "—" with no badge. Reading agreedData/faceVerified here instead
  // would claim "Matched" on PersonalDetails (where no match is performed) and
  // "Not Matched" whenever an unrelated stage — no face, multiple faces, spoof —
  // failed.
  const faceMatch = faceMatchVerdict
    ? faceMatchVerdict.verified
      ? "Matched"
      : "Not Matched"
    : null;

  return {
    verification: {
      faceMatch,
      // /verify's `score`. The panel badges it off `faceMatch` — the same
      // response's `verified` — so the number and its badge can never disagree.
      faceMatchScore: faceMatchScore ?? null,
      // /detect-spoof's `accuracy`, with that response's own spoof verdict.
      liveness: livenessResult?.score ?? null,
      livenessIsLive: livenessResult?.isLive ?? null,
      resolution: video ? formatResolution(video.size) : "—",
    },
    device: {
      microphone: formatDeviceLabel(deviceInfo?.microphone),
      speaker: formatDeviceLabel(deviceInfo?.speaker),
      camera: formatDeviceLabel(deviceInfo?.camera),
    },
    performance: { cpuStatus: cpuStatus ?? null },
    connectivity: { networkStatus: networkStatus ?? null },
    audio: audio
      ? {
          rating: rateAudio(audio),
          packetLoss: packetLossPercent(audio.packetsLost, audio.totalPackets),
          bitrate: extractBitrateKbps(audio),
        }
      : { rating: null, packetLoss: null, bitrate: null },
    video: video
      ? {
          rating: rateVideo(video),
          packetLoss: packetLossPercent(video.packetsLost, video.totalPackets),
          bitrate: extractBitrateKbps(video),
          frameRate: extractFps(video),
        }
      : { rating: null, packetLoss: null, bitrate: null, frameRate: null },
  };
}
